import { useMutation } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import { useForm } from 'react-hook-form';

import { RegionError } from '@/components/feedback/error-state';
import { Modal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { TextField } from '@/components/forms';
import { ApiError, UnauthorizedError } from '@/lib/api/errors';
import { formatPhoneUS } from '@/lib/utils/format';

import { DEALER_PROFILE_COPY, PROFILE_COPY, PROFILE_ERROR_CODES } from '../constants';
import { useIsDealer, useProfile } from '../hooks';
import type { ProfileOtpDispatch } from '../schemas';

import otpModal from './otp-modal.module.scss';
import fields from './profile-fields.module.scss';

type SendCodeStepProps = {
  title: string;
  helper: string;
  send: () => Promise<ProfileOtpDispatch>;
  onSent: (dispatch: ProfileOtpDispatch) => void;
  onCancel: () => void;
};

/** The registered number, read-only, and Send Code. Phone Step 1 (117854 §1) and, in ticket 05, the
 *  Change Password send screen (117855 §1) — one frame, different copy. */
export function SendCodeStep({ title, helper, send, onSent, onCancel }: SendCodeStepProps) {
  const profile = useProfile();
  const toast = useToast();
  const isDealer = useIsDealer();
  const mutation = useMutation({ mutationFn: send });
  const { control } = useForm({
    values: { phone: profile.data ? formatPhoneUS(profile.data.phone) : '' },
  });

  const onSend = async () => {
    try {
      onSent(await mutation.mutateAsync());
    } catch (error) {
      if (error instanceof UnauthorizedError) return; // sessionExpiry owns it (§14)
      if (error instanceof ApiError && error.code === PROFILE_ERROR_CODES.resendRateLimitExceeded) {
        toast.warning({
          title: isDealer ? DEALER_PROFILE_COPY.rateLimited : PROFILE_COPY.rateLimited,
          key: 'profile-otp',
        });
        return;
      }
      toast.error({ title: PROFILE_COPY.genericError, key: 'profile-otp' }); // §1d: stay put
    }
  };

  return (
    <Modal
      open
      onClose={onCancel}
      title={title}
      subtitle={<span className={otpModal.helper}>{helper}</span>}
      width={456}
      className={otpModal.modal}
      primaryText={PROFILE_COPY.sendCode}
      onPrimary={() => void onSend()}
      primaryDisabled={!profile.data}
      loading={mutation.isPending}
    >
      {profile.isPending ? (
        <Skeleton active title={false} paragraph={{ rows: 1 }} />
      ) : profile.isError ? (
        <RegionError
          message={PROFILE_COPY.editProfile.loadError}
          onRetry={() => void profile.refetch()}
        />
      ) : (
        <TextField
          name="phone"
          control={control}
          label="Phone Number"
          required
          disabled
          prefix="+1"
          className={fields.readOnly}
        />
      )}
    </Modal>
  );
}
